import React, { useState } from 'react';
import { Spinner } from '@billda/design-system';
import { UserDetailDto } from '../api/userManagementApi';
import { UserOverviewCard } from './panel/molecules/UserOverviewCard';
import { UserRoleCard } from './panel/molecules/UserRoleCard';
import { UserBusinessDetailsCard } from './panel/molecules/UserBusinessDetailsCard';
import { UserBusinessDetailsEmptyCard } from './panel/molecules/UserBusinessDetailsEmptyCard';
import { UserPaymentDetailsCard } from './panel/molecules/UserPaymentDetailsCard';
import { UserPaymentDetailsEmptyCard } from './panel/molecules/UserPaymentDetailsEmptyCard';
import { UserLabourGroupCard } from './panel/molecules/UserLabourGroupCard';
import { PermissionGate } from '../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { UserSiteAccessCard } from './panel/molecules/UserSiteAccessCard';
import { UserInvitationCard } from './panel/molecules/UserInvitationCard';
import { UserOffboardingCard } from './panel/molecules/UserOffboardingCard';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { UserStatus } from '../api/userManagementApi';
import {
  formatDate,
  formatDateTime,
} from '../../../shared/utils/dateFormatting';

interface UserDetailsPanelProps {
  user: UserDetailDto | null;
  loading: boolean;
}

type EditingSection = 'role' | 'sites' | 'labour' | null;

const UserDetailsPanel: React.FC<UserDetailsPanelProps> = ({
  user,
  loading,
}) => {
  const currentUser = useCurrentUser();
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const isCurrentUser = !!(
    user &&
    currentUser &&
    user.id === currentUser.userId
  );

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex justify-center p-1">
          <Spinner />
        </div>
      ) : user ? (
        <div className="flex flex-col gap-6">
          {/* User Overview Card */}
          <UserOverviewCard
            user={user}
            formatDate={formatDate}
            isCurrentUser={isCurrentUser}
          />

          {/* User Role Card */}
          <UserRoleCard
            userId={user.id}
            roles={user.roles}
            status={user.status}
            isCurrentUser={isCurrentUser}
            isEditing={editingSection === 'role'}
            onEditingChange={editing =>
              setEditingSection(editing ? 'role' : null)
            }
          />

          {user.id &&
            user.status !== UserStatus.Offboarding &&
            user.status !== UserStatus.Inactive && (
              <UserSiteAccessCard
                userId={user.id}
                userName={getUserDisplayName(user)}
                isManaging={editingSection === 'sites'}
                onManagingChange={managing =>
                  setEditingSection(managing ? 'sites' : null)
                }
              />
            )}

          {user.id &&
            user.status !== UserStatus.Offboarding &&
            user.status !== UserStatus.Inactive && (
              <UserLabourGroupCard
                userId={user.id}
                currentGroupId={user.labourGroupId}
                currentGroupName={user.labourGroupName ?? 'Unassigned'}
                isEditing={editingSection === 'labour'}
                onEditingChange={editing =>
                  setEditingSection(editing ? 'labour' : null)
                }
              />
            )}

          {user.businessDetails ? (
            <UserBusinessDetailsCard businessDetails={user.businessDetails} />
          ) : (
            <UserBusinessDetailsEmptyCard />
          )}

          <PermissionGate
            permission={Permissions.Users.PaymentDetails.Read}
            fallback={null}
          >
            {user.paymentDetails ? (
              <UserPaymentDetailsCard paymentDetails={user.paymentDetails} />
            ) : (
              <UserPaymentDetailsEmptyCard />
            )}
          </PermissionGate>

          {/* Offboarding Card (conditional) */}
          {user.offboardingInfo && (
            <UserOffboardingCard
              offboardingInfo={user.offboardingInfo}
              formatDateTime={formatDateTime}
            />
          )}

          {/* User Invitation Card (conditional) */}
          {user.invitationInfo && (
            <UserInvitationCard
              invitedByUserName={user.invitationInfo.invitedByUserName}
              invitedAt={user.invitationInfo.invitedAt}
              formatDateTime={formatDateTime}
            />
          )}
        </div>
      ) : (
        <p className="type-body-md text-center text-content-secondary">
          No user details available
        </p>
      )}
    </div>
  );
};

export default UserDetailsPanel;
